import * as React from "react";
import { useState, useEffect, useCallback, useRef } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { cn } from "@/lib/utils";
import { OptimizedImage } from "@/components/ui/OptimizedImage";

interface ImageCarouselProps {
  images: string[];
  alt: string;
  className?: string;
  aspectRatio?: string;
  children?: React.ReactNode;
  showThumbnails?: boolean;
  onIndexChange?: (index: number) => void;
  /** Use 'contain' to show full image with background fill, 'cover' to fill container with crop */
  objectFit?: 'cover' | 'contain';
  /** Priority loading for above-the-fold images */
  priority?: boolean;
}

interface ZoomState {
  scale: number;
  x: number;
  y: number;
}

export function ImageCarousel({ 
  images, 
  alt, 
  className,
  aspectRatio = "aspect-[3/4]",
  children,
  showThumbnails = false,
  onIndexChange,
  objectFit = 'cover',
  priority = false
}: ImageCarouselProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoom, setZoom] = useState<ZoomState>({ scale: 1, x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const lastTouchRef = useRef<{ x: number; y: number; distance: number } | null>(null);
  const lastTapRef = useRef<number>(0);
  const thumbnailsRef = useRef<HTMLDivElement>(null);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    const index = emblaApi.selectedScrollSnap();
    setSelectedIndex(index);
    onIndexChange?.(index);
    // Reset zoom when changing slides
    setZoom({ scale: 1, x: 0, y: 0 });
    setIsZoomed(false);
  }, [emblaApi, onIndexChange]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);

  // Update embla drag based on zoom state
  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.reInit({ watchDrag: !isZoomed });
  }, [emblaApi, isZoomed]);

  // Scroll thumbnail into view when index changes
  useEffect(() => {
    if (showThumbnails && thumbnailsRef.current) {
      const thumbnail = thumbnailsRef.current.children[selectedIndex] as HTMLElement;
      if (thumbnail) {
        thumbnail.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [selectedIndex, showThumbnails]);

  const scrollToIndex = useCallback((index: number) => {
    emblaApi?.scrollTo(index);
  }, [emblaApi]);

  const getDistance = (touches: React.TouchList) => {
    if (touches.length < 2) return 0;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const getMidpoint = (touches: React.TouchList) => {
    if (touches.length < 2) return { x: touches[0].clientX, y: touches[0].clientY };
    return {
      x: (touches[0].clientX + touches[1].clientX) / 2,
      y: (touches[0].clientY + touches[1].clientY) / 2,
    };
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      lastTouchRef.current = {
        x: getMidpoint(e.touches).x,
        y: getMidpoint(e.touches).y,
        distance: getDistance(e.touches),
      };
    } else if (e.touches.length === 1 && isZoomed) {
      lastTouchRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
        distance: 0,
      };
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!lastTouchRef.current) return;

    if (e.touches.length === 2) {
      e.preventDefault();
      const newDistance = getDistance(e.touches);
      const midpoint = getMidpoint(e.touches);
      const scaleDelta = newDistance / lastTouchRef.current.distance;
      
      setZoom(prev => {
        const newScale = Math.min(Math.max(prev.scale * scaleDelta, 1), 4);
        const isNowZoomed = newScale > 1;
        setIsZoomed(isNowZoomed);
        
        if (newScale === 1) {
          return { scale: 1, x: 0, y: 0 };
        }
        
        return {
          scale: newScale,
          x: prev.x + (midpoint.x - lastTouchRef.current!.x),
          y: prev.y + (midpoint.y - lastTouchRef.current!.y),
        };
      });

      lastTouchRef.current = {
        x: midpoint.x,
        y: midpoint.y,
        distance: newDistance,
      };
    } else if (e.touches.length === 1 && isZoomed) {
      e.preventDefault();
      const deltaX = e.touches[0].clientX - lastTouchRef.current.x;
      const deltaY = e.touches[0].clientY - lastTouchRef.current.y;
      
      setZoom(prev => ({
        ...prev,
        x: prev.x + deltaX,
        y: prev.y + deltaY,
      }));

      lastTouchRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
        distance: 0,
      };
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    // Don't interfere with interactive element taps (buttons, links)
    const target = e.target as HTMLElement;
    if (target.closest('a, button, [role="button"]')) {
      lastTouchRef.current = null;
      return;
    }

    // Double-tap to zoom
    const now = Date.now();
    if (e.touches.length === 0 && now - lastTapRef.current < 300) {
      if (isZoomed) {
        setZoom({ scale: 1, x: 0, y: 0 });
        setIsZoomed(false);
      } else {
        setZoom({ scale: 2.5, x: 0, y: 0 });
        setIsZoomed(true);
      }
    }
    lastTapRef.current = now;
    
    if (e.touches.length < 2) {
      lastTouchRef.current = null;
    }
    
    // Clamp position when zoom ends
    if (e.touches.length === 0 && zoom.scale <= 1) {
      setZoom({ scale: 1, x: 0, y: 0 });
      setIsZoomed(false);
    }
  };

  // If only one image, render without carousel
  if (images.length <= 1) {
    return (
    <div 
      ref={containerRef}
      className={cn("relative rounded-2xl overflow-hidden", isZoomed && "touch-none", aspectRatio, className)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <OptimizedImage
          src={images[0] || '/placeholder.svg'}
          alt={alt}
          priority={priority}
          className={cn(
            "w-full h-full transition-transform duration-100",
            objectFit === 'contain' ? "object-contain bg-muted" : "object-cover"
          )}
          style={{
            transform: `scale(${zoom.scale}) translate(${zoom.x / zoom.scale}px, ${zoom.y / zoom.scale}px)`,
          }}
        />
        {!isZoomed && children}
        {isZoomed && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-full z-20">
            Double-tap to reset
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col">
    <div 
      ref={containerRef}
      className={cn("relative rounded-2xl overflow-hidden", isZoomed && "touch-none", aspectRatio, className)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div ref={emblaRef} className="overflow-hidden h-full">
          <div className="flex h-full">
            {images.map((image, index) => (
              <div 
                key={index} 
                className="flex-[0_0_100%] min-w-0 h-full"
              >
                <OptimizedImage
                  src={image}
                  alt={`${alt} - Image ${index + 1}`}
                  priority={priority && index === 0}
                  className={cn(
                    "w-full h-full transition-transform duration-100",
                    objectFit === 'contain' ? "object-contain bg-muted" : "object-cover"
                  )}
                  style={selectedIndex === index ? {
                    transform: `scale(${zoom.scale}) translate(${zoom.x / zoom.scale}px, ${zoom.y / zoom.scale}px)`,
                  } : undefined}
                />
              </div>
            ))}
          </div>
        </div>
        
        {/* Dot indicators - hide when zoomed or showing thumbnails */}
        {!isZoomed && !showThumbnails && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => scrollToIndex(index)}
                className={cn(
                  "w-2 h-2 rounded-full transition-all duration-300",
                  selectedIndex === index 
                    ? "bg-white w-4" 
                    : "bg-white/50 hover:bg-white/70"
                )}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </div>
        )}
        
        {/* Zoom hint */}
        {isZoomed && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-full z-20">
            Double-tap to reset
          </div>
        )}
        
        {/* Children (overlays, badges) - hide when zoomed */}
        {!isZoomed && children}
      </div>

      {/* Thumbnail strip - below carousel */}
      {showThumbnails && images.length > 1 && !isZoomed && (
        <div 
          ref={thumbnailsRef}
          className="flex gap-3 mt-3 overflow-x-auto scrollbar-hide px-1 py-1"
        >
          {images.map((image, index) => (
            <button
              key={index}
              onClick={() => scrollToIndex(index)}
              className={cn(
                "flex-shrink-0 w-16 h-16 rounded-xl overflow-hidden transition-all duration-200",
                selectedIndex === index 
                  ? "ring-2 ring-foreground/30" 
                  : "opacity-60 active:scale-95"
              )}
              aria-label={`View image ${index + 1}`}
            >
              <img
                src={image}
                alt={`${alt} thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
                draggable={false}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
